import type { ResultLink } from '@/payment/payment-steps';
import { OUTLINE_PILL, PRIMARY_PILL } from '@/payment/payment-styles';

/**
 * GAS で動いている（動き終えた）ジョブの表示内容。
 */
export interface PaymentJobView {
  /** 処理の名前（レポート作成・証憑整理など） */
  title: string;
  /** running=動いている / done=終わった / error=失敗した */
  phase: 'running' | 'done' | 'error';
  /** いまの工程 */
  step: string;
  /** 件数の進み */
  current: number | null;
  /** 件数の全体 */
  total: number | null;
  /** 終わった・失敗したときの文言 */
  message: string;
  /** 作ったレポート・フォルダへのリンク */
  links: readonly ResultLink[];
}

/**
 * PaymentJobPanel の props。
 */
export interface PaymentJobPanelProps {
  /** 表示するジョブ */
  job: PaymentJobView;
  /** 真ん中に大きく出すか（false なら右下に小さく出す） */
  expanded: boolean;
  /** 右下から真ん中に戻す */
  onExpand: () => void;
  /** 真ん中から右下に退ける */
  onMinimize: () => void;
  /** 閉じる（動いている間は呼ばれない） */
  onClose: () => void;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 進みの割合（0〜100）を出す。件数が分からなければ null
 * @graph-connects none
 */
export function progressPercent(job: Pick<PaymentJobView, 'current' | 'total'>): number | null {
  return job.current !== null && job.total !== null && job.total > 0
    ? Math.min(100, Math.round((job.current / job.total) * 100))
    : null;
}

/**
 * 進みの棒。件数が分からないときは流れる棒にする
 * @graph-connects none
 */
function ProgressBar({ percent }: { percent: number | null }) {
  return (
    <div className="h-2 overflow-hidden rounded-full bg-[var(--color-doing-bg)]">
      <div
        className={
          percent === null
            ? 'h-full w-1/3 animate-pulse rounded-full bg-[var(--color-doing-border)]'
            : 'h-full rounded-full bg-[var(--color-doing-border)] transition-[width] duration-500'
        }
        style={percent === null ? undefined : { width: `${String(percent)}%` }}
      />
    </div>
  );
}

/** 状態ごとの札 */
/** @graph-connects none */
const PHASE_CHIP: Record<PaymentJobView['phase'], { label: string; className: string }> = {
  running: {
    label: '実行中',
    className: 'bg-[var(--color-doing-bg)] text-[var(--color-doing-text)]',
  },
  done: {
    label: '完了',
    className: 'bg-[var(--color-kind-meeting-bg)] text-[var(--color-kind-meeting-text)]',
  },
  error: {
    label: '失敗',
    className: 'bg-[var(--color-kind-closing-bg)] text-[var(--color-overdue)]',
  },
};

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business GAS で動いているジョブの進み。始めは真ん中に出し、外側を押すと右下に小さく退いて、その間もほかの操作ができる。右下の表示を押すと真ん中に戻る。終わったら結果とリンクを出す
 * @graph-connects progressPercent [calls] 進みの割合を出す
 */
export function PaymentJobPanel({
  job,
  expanded,
  onExpand,
  onMinimize,
  onClose,
}: PaymentJobPanelProps) {
  const percent = progressPercent(job);
  const chip = PHASE_CHIP[job.phase];

  if (!expanded) {
    return (
      <aside
        aria-label={`${job.title}（${chip.label}）`}
        className="fixed right-5 bottom-5 z-20 w-72 rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-4 py-3 shadow-[0_8px_24px_var(--color-shadow)]"
      >
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${chip.className}`}>
            {chip.label}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[var(--color-text-primary)]">
            {job.title}
          </span>
          {job.phase === 'running' ? null : (
            <button
              type="button"
              aria-label="閉じる"
              onClick={onClose}
              className="shrink-0 px-1 text-base leading-none text-[var(--color-text-muted)]"
            >
              ×
            </button>
          )}
        </div>
        {job.phase === 'running' ? (
          <div className="mt-2">
            <ProgressBar percent={percent} />
            <p className="mt-1 mb-0 truncate text-xs text-[var(--color-text-secondary)]">
              {job.step}
              {percent === null ? '' : `（${String(job.current)} / ${String(job.total)}）`}
            </p>
          </div>
        ) : null}
        <button
          type="button"
          onClick={onExpand}
          className="mt-2 text-xs text-[var(--color-accent)] underline"
        >
          {job.phase === 'running' ? '大きく表示' : '結果を見る'}
        </button>
      </aside>
    );
  }

  return (
    <>
      <button
        type="button"
        aria-label="外側（押すと右下に表示）"
        onClick={onMinimize}
        className="fixed inset-0 z-10 cursor-default bg-[var(--color-text-primary)]/20"
      />
      <aside
        role="dialog"
        aria-label={job.title}
        className="fixed top-1/2 left-1/2 z-20 w-[min(30rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-3xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-7 py-6 shadow-[0_12px_40px_var(--color-shadow)]"
      >
        <div className="mb-3 flex items-center gap-2">
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${chip.className}`}>
            {chip.label}
          </span>
          <h2 className="m-0 text-base font-semibold text-[var(--color-text-primary)]">
            {job.title}
          </h2>
        </div>

        {job.phase === 'running' ? (
          <div role="status">
            <p className="m-0 mb-3 text-sm text-[var(--color-text-primary)]">{job.step}</p>
            <ProgressBar percent={percent} />
            {percent === null ? null : (
              <p className="mt-1 mb-0 text-right text-xs tabular-nums text-[var(--color-text-muted)]">
                {job.current} / {job.total}
              </p>
            )}
            <p className="mt-4 mb-0 text-xs leading-relaxed text-[var(--color-text-muted)]">
              GAS で動いています。外側を押すと右下に表示され、その間もほかの操作ができます。
            </p>
            <div className="mt-4 flex justify-end">
              <button type="button" onClick={onMinimize} className={OUTLINE_PILL}>
                右下に表示する
              </button>
            </div>
          </div>
        ) : (
          <>
            <p
              role="status"
              className={
                job.phase === 'error'
                  ? 'm-0 text-sm leading-relaxed whitespace-pre-line text-[var(--color-overdue)]'
                  : 'm-0 text-sm leading-relaxed whitespace-pre-line text-[var(--color-text-primary)]'
              }
            >
              {job.message}
            </p>
            {job.links.length === 0 ? null : (
              <div className="mt-4 flex flex-wrap gap-2">
                {job.links.map((link) => (
                  <a
                    key={link.url}
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className={OUTLINE_PILL}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            )}
            <div className="mt-5 flex justify-end">
              <button type="button" onClick={onClose} className={PRIMARY_PILL}>
                閉じる
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
